/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect, useHistory, useLocation } from "react-router-dom";
import NotFound from "components/NotFound";
import MenuBar from "./MenuBar";
import { ModulesProvider } from "./ModulesContext";
import AppContext from "AppContext";
import List from "./List";
import PersonalInfo from "./PersonalInfo";
import Accounting from "./Accounting";
import ProgressInfo from "./ProgressInfo";
import Reports from "./Reports";
import Attachments from "./Attachments";
import { notifyError, notifyInfo, notifySuccess } from "redux/actions/Notifications";
import { useDispatch, useSelector } from "react-redux";
import { getForm, getDataNormalizer, getListHead, getFiltersBuilder } from "../Forms";
import Workflow from "./Workflow";
import { getAdvancedFiltersContext } from "./List/AdvancedFiltersContexts";
import Chat from "./Chat";
import ClientInfoView from "../Forms/ClientInfoView";
import { Tenant } from "utils/tenants";
import ChainOfCustody from "./ChainOfCustody";
import Status from "components/common_components/Status";
import { MenuItem, TextField } from "@material-ui/core";
import Header from "components/common_components/Header";
import { hasAttributes } from "utils/objects";
import DatePicker from "components/common_components/DatePicker/DatePicker";
import Autocomplete from "components/common_components/Autocomplete/Autocomplete";
import UserOption from "components/common_components/UserSelect/UserOption";
import { formatCurrency } from "utils/currency";
import { hasPermission } from "utils/session";

function Modules({ match, dbName, formName, code, name }) {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const [fileCases, setFileCases] = useState(undefined);
    const [currentFileCase, setCurrentFileCase] = useState(undefined);
    const [resources, setResources] = useState({});
    const [accountingData, setAccountingData] = useState(undefined);
    const [balance, setBalance] = useState(undefined);
    const [progressInfoData, setProgressInfoData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageableData, setPageableData] = useState(undefined);
    const [pageableAccountingData, setPageableAccountingData] = useState(undefined);
    const [pageableProgressInfoData, setPageableProgressInfoData] = useState(undefined);
    const [clientPhoto, setClientPhoto] = useState(undefined);
    const [attachments, setAttachments] = useState(undefined);
    const [workflow, setWorkflow] = useState(undefined);
    const [quickSearchValue, setQuickSearchValue] = useState("");
    const [advancedFilters, setAdvancedFilters] = useState(undefined);
    const AdvancedFiltersContext = getAdvancedFiltersContext(formName);
    const [changeStatusLoading, setChangeStatusLoading] = useState(false);
    // const [changeAssigneeLoading, setChangeAssigneeLoading] = useState(false);

    function buildFilters() {
        return hasAttributes(advancedFilters)
            ? { ...advancedFilters, isQuickSearch: false, quickSearchValue: null }
            : getFiltersBuilder(formName)(quickSearchValue);
    }

    const defaultRequestData = {
        filter: buildFilters(),
        orderBy: "id",
        orderDirection: "desc",
        pageNumber: 0,
        pageSize: 5,
    };

    const {
        api: {
            Modules,
            Management: { Customers },
            General: { Log, Storage },
            Workflows: { Workflows },
        },
        module,
    } = useContext(AppContext);

    const DB = Modules(dbName);

    function getFileCases(requestData = defaultRequestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        DB.PersonalInfo.getList({
            onSuccess: (data, pageableData) => {
                setFileCases(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
            normalizeData: getDataNormalizer(formName),
        });
    }

    async function getFileCasesAsync(requestData = defaultRequestData) {
        return DB.PersonalInfo.getListAsync(requestData, getDataNormalizer(formName));
    }

    useEffect(() => {
        const { search } = location;
        if (search) {
            try {
                const queryParams = new URLSearchParams(search);
                const fileCaseId = queryParams.get("fileCaseId");
                const openTab = queryParams.get("openTab");
                const loadEntity = async () => {
                    setIsLoading(true);
                    const res = await DB.PersonalInfo.getOneByIdAsync(fileCaseId, getDataNormalizer(`${formName}_Single`));
                    setCurrentFileCase(res);
                    if (openTab) {
                        goToTab(openTab);
                    } else {
                        goToDetailHandler();
                    }
                };

                loadEntity();
            } catch (error) {
                dispatch(notifyError(error));
            } finally {
                setIsLoading(false);
            }
        } else {
            getFileCases();
        }

        return () => {
            setFileCases([]);
        };
    }, []);

    function getResources() {
        DB.PersonalInfo.getResources({
            moduleCode: module.code,
            onSuccess: (data) => {
                setResources(data);
            },
            onError: (error) => {
                setResources({});
                dispatch(notifyError(error));
            },
        });
    }

    async function getBalanceAsync() {
        return DB.Accounting.getBalanceAsync(currentFileCase.id);
    }

    async function getBalance() {
        try {
            const newBalance = await getBalanceAsync();
            setBalance(newBalance);
        } catch (error) {
            setBalance(0);
        }
    }

    function getAccounting(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        DB.Accounting.getList({
            personalInfoId: currentFileCase.id,
            onSuccess: async (data, pageableData) => {
                await getBalance();
                setAccountingData(data);
                setPageableAccountingData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                setPageableAccountingData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    function getProgressInfo(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        DB.ProgressInfo.getList({
            personalInfoId: currentFileCase.id,
            onSuccess: (data, pageableData) => {
                setProgressInfoData(data);
                setPageableProgressInfoData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                setPageableProgressInfoData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    async function getClientPhotoAsync(clientId) {
        try {
            const photoURL = await Storage.getDownloadURLAsync({
                dirName: "management/clients/images",
                fileName: `client_id_${clientId || currentFileCase.client.id}`,
            });
            setClientPhoto(photoURL);
        } catch (message) {}
    }

    async function getAttachmentsAsync() {
        try {
            const _attachments = await DB.Attachments.getAllByFileCaseIdAsync(currentFileCase.id);
            setAttachments(_attachments);
        } catch (message) {}
    }

    async function getWorkflowAsync(newTrnFlowId) {
        if (newTrnFlowId || currentFileCase.trnFlowId) {
            try {
                setIsLoading(true);
                const _workflow = await Workflows.getOneByIdAsync({
                    id: newTrnFlowId || currentFileCase.trnFlowId,
                });
                setWorkflow(_workflow);
            } catch (message) {
            } finally {
                setIsLoading(false);
            }
        } else {
            setWorkflow(undefined);
        }
    }

    async function restartWorkflowAsync() {
        if (currentFileCase.trnFlowId) {
            try {
                setIsLoading(true);
                const res = await Workflows.restart({
                    id: currentFileCase.trnFlowId,
                });
                if (res) {
                    dispatch(notifySuccess("Workflow restarted successfully!"));
                    await getWorkflowAsync();
                }
            } catch (error) {
                const customError = error && error.response && error.response.data && error.response.data.message;
                dispatch(notifyError(customError || error));
            } finally {
                setIsLoading(false);
            }
        }
    }

    async function startWorkflowAsync() {
        try {
            setIsLoading(true);
            const response = await DB.PersonalInfo.startNewWorkflow(currentFileCase.id);
            const newTrnFlowId = response && response.data;
            if (newTrnFlowId) {
                dispatch(notifySuccess("Workflow started successfully!"));
                setCurrentFileCase({
                    ...currentFileCase,
                    trnFlowId: newTrnFlowId,
                });
                await getWorkflowAsync(newTrnFlowId);
            }
        } catch (error) {
            const customError = error && error.response && error.response.data && error.response.data.message;
            dispatch(notifyError(customError || error));
        } finally {
            setIsLoading(false);
        }
    }

    async function startConversationAsync(userId) {
        try {
            setIsLoading(true);
            const response = await DB.PersonalInfo.startNewConversation(currentFileCase.id, userId);
            const newModuleConversation = response && response.data;
            if (newModuleConversation) {
                dispatch(notifySuccess("Conversation started successfully!"));
                // Reload the full conversations list (new active + closed history) instead of
                // replacing with a single one, so Twilio re-materializes and the render rules see all
                const res = await DB.PersonalInfo.getOneByIdAsync(currentFileCase.id, getDataNormalizer(`${formName}_Single`));
                setCurrentFileCase((prev) => ({
                    ...prev,
                    client: { ...prev.client, conversations: res?.client?.conversations || [] },
                }));
            }
        } catch (error) {
            const customError = error && error.response && error.response.data && error.response.data.message;
            dispatch(notifyError(customError || error));
        } finally {
            setIsLoading(false);
        }
    }

    function restartData() {
        setAccountingData(undefined);
        setProgressInfoData(undefined);
        setBalance(undefined);
        setClientPhoto(undefined);
        setWorkflow(undefined);
    }

    useEffect(() => {
        if (currentFileCase) {
            setClientPhoto(undefined);
            getAccounting();
            getProgressInfo();
            getAttachmentsAsync();
            getClientPhotoAsync();
            getWorkflowAsync();
        } else {
            restartData();
        }
    }, [currentFileCase]);

    useEffect(() => {
        if (module) {
            getResources();
        }
    }, [module]);

    const changeAssigneeUser = async (assigneeUser) => {
        try {
            // setChangeAssigneeLoading(true);
            await DB.PersonalInfo.changeAssigneeUser(currentFileCase.id, assigneeUser?.id || null);
            setCurrentFileCase((prev) => ({ ...prev, assigneeUser }));
            dispatch(notifySuccess("Assignee changed."));
        } catch (error) {
            dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
        }
        // finally {
        //     setChangeAssigneeLoading(false);
        // }
    };

    const isModuleAdmin = hasPermission(user, `modules.${dbName}.admin`);
    const hasModulePermission = (permission) => isModuleAdmin || hasPermission(user, `modules.${dbName}.${permission}`);

    const permissions = {
        admin: isModuleAdmin,
        header: {
            actions: {
                changeStatus: hasModulePermission("header.actions.change-status"),
                changeAssignee: hasModulePermission("header.actions.change-assignee"),
                changeDueDate: hasModulePermission("header.actions.change-due-date"),
            },
        },
        list: {
            actions: {
                add: hasModulePermission("list.actions.add"),
                delete: hasModulePermission("list.actions.delete"),
            },
        },
        personalInfo: {
            access: hasModulePermission("personal-info.access"),
            actions: {
                edit: hasModulePermission("personal-info.actions.edit"),
            },
        },
        attachments: {
            access: hasModulePermission("attachments.access"),
        },
        accounting: {
            access: hasModulePermission("accounting.access"),
            payment: {
                method: {
                    annulment: hasModulePermission("accounting.payment.method.annulment"),
                },
            },
        },
        progressInfo: {
            access: hasModulePermission("progress-info.access"),
        },
        reports: {
            access: hasModulePermission("reports.access"),
        },
        workflow: {
            access: hasModulePermission("workflow.access"),
        },
        chat: {
            access: hasModulePermission("chat.access"),
        },
        chainOfCustody: {
            access: hasModulePermission("chain-of-custody.access"),
        },
    };

    const header = currentFileCase && (
        <div className="form-head">
            <div className="item">
                <h1>{currentFileCase.fileNo}</h1>
            </div>
            <div className="item">
                <TextField
                    select
                    value={currentFileCase.workStatus}
                    onChange={(e) => changeWorkStatus(e.target.value)}
                    size="small"
                    className={"status-select"}
                    label=""
                    disabled={!permissions.header.actions.changeStatus}
                >
                    {["casePreparation", "gettingAttorneyReview", "approved", "rejected", "completed"].map((value, index) => (
                        <MenuItem value={value} key={index}>
                            {changeStatusLoading && <div className="icon icon--loading loading" />}
                            <Status id={value} withIcon />
                        </MenuItem>
                    ))}
                </TextField>
            </div>
            <div className="item">
                <Autocomplete
                    value={currentFileCase.assigneeUser}
                    options={getUsersOptions()}
                    valueKey="id"
                    labelKey="name"
                    onChange={changeAssigneeUser}
                    label="Assignee"
                    variant="standard"
                    renderOption={(user) => <UserOption {...user} />}
                    getOptionLabel={(option) => (option ? option.name : "")}
                    getOptionSelected={(opt, val) => opt.id === val.id}
                    disabled={!permissions.header.actions.changeAssignee}
                />
            </div>
            <div className="item">
                <DatePicker
                    label="Due Date"
                    value={currentFileCase.dueDate}
                    onChange={(dueDate) => changeDueDate(dueDate)}
                    inputVariant="standard"
                    disabled={!permissions.header.actions.changeDueDate}
                />
            </div>
            <div className="item">
                <ClientInfoView client={currentFileCase.client} balance={formatCurrency(balance)} />
            </div>
        </div>
    );

    function redirect(path) {
        history.push(`${match.path}${path}`);
    }

    function goToDetailHandler() {
        redirect("/personal-info/detail");
    }

    function goToTab(tabName) {
        redirect(`/${tabName}`);
    }

    function goToAddHandler() {
        restartData();
        redirect("/personal-info/add");
    }

    function goToUpdateHandler() {
        redirect("/personal-info/update");
    }

    function cancelHandler() {
        redirect("/list");
    }

    const changeWorkStatus = async (workStatus) => {
        try {
            setChangeStatusLoading(true);
            await DB.PersonalInfo.changeWorkStatus(currentFileCase.id, workStatus);
            setCurrentFileCase((prev) => ({ ...prev, workStatus }));
            dispatch(notifySuccess("Status changed."));
        } catch (error) {
            dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
        } finally {
            setChangeStatusLoading(false);
        }
    };

    const changeDueDate = async (dueDate) => {
        try {
            console.log(dueDate);

            await DB.PersonalInfo.changeDueDate(currentFileCase.id, dueDate || null);
            setCurrentFileCase((prev) => ({ ...prev, dueDate }));
            dispatch(notifySuccess("Due date changed."));
        } catch (error) {
            dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
        }
    };

    function getUsersOptions() {
        return resources.users || [];
    }

    return (
        <ModulesProvider
            value={{
                ...DB,
                permissions,
                Customers,
                personalInfoData: fileCases,
                getFileCases,
                getFileCasesAsync,
                currentFileCase,
                clientPhoto,
                getClientPhotoAsync,
                setCurrentFileCase,
                resources,
                setResources,
                getAccounting,
                accountingData,
                pageableAccountingData,
                balance,
                getBalanceAsync,
                getProgressInfo,
                progressInfoData,
                pageableProgressInfoData,
                isLoading,
                header,
                module,
                pageableData,
                Log,
                moduleCode: code,
                attachments,
                getAttachmentsAsync,
                Storage,
                workflow,
                getWorkflowAsync,
                restartWorkflowAsync,
                startWorkflowAsync,
                startConversationAsync,
            }}
        >
            <AdvancedFiltersContext.Provider value={{ advancedFilters, setAdvancedFilters }}>
                <div className="module-page">
                    {!currentFileCase && <Header title={name} />}
                    <MenuBar>
                        <Switch>
                            <Redirect exact from={match.path} to={`${match.path}/list`} />
                            <Route
                                exact
                                path={`${match.path}/list`}
                                render={(props) => (
                                    <List
                                        {...props}
                                        goToDetail={goToDetailHandler}
                                        goToAdd={goToAddHandler}
                                        listHead={getListHead(formName)}
                                        filterBuilder={getFiltersBuilder(formName)}
                                        filterValue={quickSearchValue}
                                        onFilterChange={setQuickSearchValue}
                                        formName={formName}
                                    />
                                )}
                            />
                            {permissions.personalInfo.access ? (
                                <Route
                                    path={`${match.path}/personal-info`}
                                    render={(props) => (
                                        <PersonalInfo
                                            {...props}
                                            onCancel={cancelHandler}
                                            goToDetail={goToDetailHandler}
                                            goToUpdate={permissions.personalInfo.actions.edit ? goToUpdateHandler : undefined}
                                            formComponent={getForm(formName)}
                                        />
                                    )}
                                />
                            ) : (
                                <Redirect from={`${match.path}/personal-info`} to={`${match.path}/list`} />
                            )}
                            {permissions.attachments.access && currentFileCase ? (
                                <Route
                                    exact
                                    path={`${match.path}/attachments`}
                                    render={(props) => <Attachments {...props} onCancel={cancelHandler} />}
                                />
                            ) : (
                                <Redirect exact from={`${match.path}/attachments`} to={`${match.path}/list`} />
                            )}
                            {permissions.accounting.access && currentFileCase ? (
                                <Route
                                    exact
                                    path={`${match.path}/accounting`}
                                    render={(props) => <Accounting {...props} onCancel={cancelHandler} />}
                                />
                            ) : (
                                <Redirect exact from={`${match.path}/accounting`} to={`${match.path}/list`} />
                            )}
                            {permissions.progressInfo.access && currentFileCase ? (
                                <Route
                                    exact
                                    path={`${match.path}/progress-info`}
                                    render={(props) => <ProgressInfo {...props} onCancel={cancelHandler} />}
                                />
                            ) : (
                                <Redirect exact from={`${match.path}/progress-info`} to={`${match.path}/list`} />
                            )}
                            {permissions.reports.access && currentFileCase ? (
                                <Route exact path={`${match.path}/reports`} component={Reports} />
                            ) : (
                                <Redirect exact from={`${match.path}/reports`} to={`${match.path}/list`} />
                            )}
                            {permissions.workflow.access && currentFileCase ? (
                                <Route
                                    exact
                                    path={`${match.path}/workflow`}
                                    render={(props) => <Workflow {...props} onCancel={cancelHandler} />}
                                />
                            ) : (
                                <Redirect exact from={`${match.path}/workflow`} to={`${match.path}/list`} />
                            )}
                            {permissions.chat.access && currentFileCase ? (
                                <Route exact path={`${match.path}/chat`} render={(props) => <Chat {...props} onCancel={cancelHandler} />} />
                            ) : (
                                <Redirect exact from={`${match.path}/chat`} to={`${match.path}/list`} />
                            )}
                            {Tenant.getFeatures().chainOfCustody.enabled && permissions.chainOfCustody.access && currentFileCase ? (
                                <Route
                                    path={`${match.path}/chain-of-custody`}
                                    render={(props) => <ChainOfCustody {...props} onCancel={cancelHandler} />}
                                />
                            ) : (
                                <Redirect exact from={`${match.path}/chain-of-custody`} to={`${match.path}/list`} />
                            )}
                            <Route component={NotFound} />
                        </Switch>
                    </MenuBar>
                </div>
            </AdvancedFiltersContext.Provider>
        </ModulesProvider>
    );
}

Modules.propTypes = {
    dbName: PropTypes.string.isRequired,
    formName: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
};

export default Modules;
